import { useState, useRef, useEffect } from "react";
import api, { apiErr } from "@/lib/api";
import { Bot, Send, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

export function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", text: "Hi! I'm the Certalyth assistant. I can help with questions about courses, exams, payments, or policies. How can I help you?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sid, setSid] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMsgs((m) => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: text, session_id: sid });
      setSid(data.session_id);
      setMsgs((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", text: apiErr(e.response?.data?.detail) || "Connection error." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} data-testid="ai-assistant-toggle-button"
        className="fixed bottom-6 right-6 z-[80] h-14 w-14 rounded-full btn-gold flex items-center justify-center shadow-2xl pulse-glow">
        {open ? <X className="h-6 w-6" /> : <Bot className="h-6 w-6" />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-[80] w-[92vw] max-w-md h-[70vh] max-h-[560px] glass rounded-2xl flex flex-col overflow-hidden shadow-2xl animate-fadeup" data-testid="ai-assistant-panel">
          <div className="flex items-center gap-3 px-4 py-3 bg-surface-2 border-b border-white/10">
            <div className="h-9 w-9 rounded-full bg-gold/20 flex items-center justify-center"><Sparkles className="h-4 w-4 text-gold" /></div>
            <div>
              <p className="font-display font-semibold text-white text-sm">Certalyth AI Assistant</p>
              <p className="text-xs text-slate-400">Courses • Exams • Policies</p>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-messages">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-electric text-white" : "bg-surface-2 text-slate-200 border border-white/10"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && <div className="text-xs text-slate-400 animate-pulse">Assistant is typing…</div>}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-white/10 flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask a question…" data-testid="ai-chat-input"
              className="flex-1 bg-surface-2 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-gold" />
            <Button onClick={send} disabled={loading} className="btn-gold" data-testid="ai-chat-send-button"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </>
  );
}
